<template>
  <div class="project-data">
    <div class="page-header">
      <h3>数据管理</h3>
      <div class="header-actions">
        <el-button icon="el-icon-download" @click="exportAll">导出全部数据</el-button>
      </div>
    </div>

    <!-- 问卷选择器 -->
    <div class="form-selector">
      <span class="label">选择问卷：</span>
      <el-select v-model="selectedFormKey" placeholder="请选择问卷" @change="loadFormData" clearable>
        <el-option 
          v-for="form in forms" 
          :key="form.formKey" 
          :label="stripHtml(form.name)" 
          :value="form.formKey"
        ></el-option>
      </el-select>
      <el-button 
        v-if="selectedFormKey" 
        type="primary" 
        icon="el-icon-download" 
        @click="exportFormData"
      >
        导出当前问卷
      </el-button>
    </div>

    <!-- 筛选区域（仅考试问卷显示） -->
    <div class="filter-bar" v-if="selectedFormKey && isExamForm">
      <el-input 
        v-model="searchKeyword" 
        placeholder="搜索店名/店代码" 
        prefix-icon="el-icon-search"
        clearable
        style="width: 200px"
        @clear="handleSearch"
        @keyup.enter.native="handleSearch"
      ></el-input>
      <el-select v-model="reviewFilter" placeholder="审核状态筛选" clearable @change="handleSearch" style="width: 160px">
        <el-option label="待审核" :value="0"></el-option>
        <el-option label="一审未完成" value="review1"></el-option>
        <el-option label="二审未完成" value="review2"></el-option>
        <el-option label="三审未完成" value="review3"></el-option>
        <el-option label="三审已完成" value="completed"></el-option>
      </el-select>
      <el-select v-model="appealFilter" placeholder="申诉状态筛选" clearable @change="handleSearch" style="width: 160px">
        <el-option label="已下发申诉" value="appealed"></el-option>
        <el-option label="未下发申诉" value="not_appealed"></el-option>
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
      <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
      <el-button 
        type="warning" 
        icon="el-icon-s-promotion" 
        @click="showAppealDialog"
        :disabled="selectedRows.length === 0"
      >
        下发申诉 ({{ selectedRows.length }})
      </el-button>
    </div>


    <!-- 数据统计概览 -->
    <div class="stats-row" v-if="selectedFormKey">
      <div class="stat-card">
        <div class="stat-value">{{ dataStats.total }}</div>
        <div class="stat-label">总答卷数</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ dataStats.today }}</div>
        <div class="stat-label">今日新增</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ dataStats.week }}</div>
        <div class="stat-label">本周新增</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ formatDuration(dataStats.avgDuration) }}</div>
        <div class="stat-label">平均用时</div>
      </div>
    </div>

    <!-- 数据表格 -->
    <div class="data-table" v-if="selectedFormKey">
      <el-table :data="tableData" v-loading="loading" border stripe style="width: 100%" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="50" v-if="isExamForm"></el-table-column>
        <el-table-column type="index" label="序号" width="60"></el-table-column>
        <el-table-column label="经销商代码" min-width="120">
          <template slot-scope="{ row }">
            {{ row.dealerCode || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="经销商名称" min-width="150">
          <template slot-scope="{ row }">
            {{ row.dealerName || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="提交时间" min-width="150">
          <template slot-scope="{ row }">
            {{ formatTime(row.submitTime || row.createdTime) }}
          </template>
        </el-table-column>
        <el-table-column label="IP地址" min-width="120">
          <template slot-scope="{ row }">
            {{ row.ipAddress || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="用时" min-width="80">
          <template slot-scope="{ row }">
            {{ formatDuration(row.duration) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" min-width="140">
          <template slot-scope="{ row }">
            <div class="status-tags">
              <el-tag :type="row.status === 2 ? 'success' : 'info'" size="small">
                {{ row.status === 2 ? '已提交' : '进行中' }}
              </el-tag>
              <el-tag 
                v-if="appealStatusMap[row.answerKey]" 
                type="warning" 
                size="small"
              >
                已下发申诉
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <!-- 审核状态列（仅考试问卷显示） -->
        <el-table-column label="一审" width="70" v-if="isExamForm" align="center">
          <template slot-scope="{ row }">
            <i v-if="row.auditStatus >= 1" class="el-icon-check review-done"></i>
            <i v-else class="el-icon-close review-pending"></i>
          </template>
        </el-table-column>
        <el-table-column label="二审" width="70" v-if="isExamForm" align="center">
          <template slot-scope="{ row }">
            <i v-if="row.auditStatus >= 2" class="el-icon-check review-done"></i>
            <i v-else class="el-icon-close review-pending"></i>
          </template>
        </el-table-column>
        <el-table-column label="三审" width="70" v-if="isExamForm" align="center">
          <template slot-scope="{ row }">
            <i v-if="row.auditStatus >= 3" class="el-icon-check review-done"></i>
            <i v-else class="el-icon-close review-pending"></i>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="viewDetail(row)">查看</el-button>
            <el-button type="text" @click="reviewAnswer(row)" v-if="isExamForm">审核</el-button>
            <el-button type="text" class="text-danger" @click="deleteAnswer(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page.sync="page"
          :page-size="pageSize"
          :total="total"
          @current-change="loadFormData"
        ></el-pagination>
      </div>
    </div>

    <!-- 空状态提示 -->
    <div class="empty-state" v-if="!selectedFormKey">
      <i class="el-icon-document"></i>
      <p>请选择一个问卷查看数据</p>
    </div>

    <!-- 答卷详情对话框 -->
    <el-dialog title="答卷详情" :visible.sync="detailDialogVisible" width="800px">
      <div v-if="currentAnswer" class="answer-detail">
        <div class="detail-header">
          <div class="info-item">
            <label>答卷编号：</label>
            <span>{{ currentAnswer.answerNo }}</span>
          </div>
          <div class="info-item">
            <label>提交时间：</label>
            <span>{{ formatTime(currentAnswer.submitTime || currentAnswer.createdTime) }}</span>
          </div>
          <div class="info-item">
            <label>IP地址：</label>
            <span>{{ currentAnswer.ipAddress || '-' }}</span>
          </div>
        </div>
        <el-divider></el-divider>
        <div class="answer-content">
          <div v-for="(item, index) in currentAnswerItems" :key="index" class="answer-item">
            <div class="question-title">{{ index + 1 }}. {{ stripHtml(item.title) }}</div>
            <div class="answer-value">{{ formatAnswerValue(item) }}</div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 导出进度遮罩 -->
    <div class="export-overlay" v-if="exporting">
      <div class="export-progress-card">
        <i class="el-icon-loading"></i>
        <p class="export-title">正在导出数据...</p>
        <el-progress :percentage="exportProgress" :stroke-width="8"></el-progress>
        <p class="export-tip">大数据量导出可能需要较长时间，请耐心等待</p>
      </div>
    </div>

    <!-- 下发申诉对话框 -->
    <el-dialog title="下发申诉" :visible.sync="appealDialogVisible" width="500px">
      <el-form :model="appealForm" label-width="100px">
        <el-form-item label="批次名称">
          <el-input v-model="appealForm.batchName" placeholder="如：2024年第一季度暗访申诉" maxlength="100"></el-input>
        </el-form-item>
        <el-form-item label="选中答卷">
          <span>{{ selectedRows.length }} 份</span>
        </el-form-item>
        <el-form-item label="审核层级">
          <el-tag>{{ getAuditLevelText(selectedAuditLevel) }}</el-tag>
        </el-form-item>
        <el-form-item label="申诉开始时间">
          <el-date-picker
            v-model="appealForm.startTime"
            type="datetime"
            placeholder="选择开始时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="申诉截止时间">
          <el-date-picker
            v-model="appealForm.endTime"
            type="datetime"
            placeholder="选择截止时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="appealDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAppeal" :loading="appealSubmitting">确认下发</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { pageFormRequest } from 'index/api/project/form'
import axios from 'index/axios'
import rawAxios from 'axios' // 原生 axios 用于 blob 下载

// 答卷相关API
function getAnswerPage(data) {
  return axios.post('/user/form/answer/page', data)
}

function getStatisticsOverview(formKey) {
  return axios.get(`/user/form/statistics/overview/${formKey}`)
}

function getAnswerDetail(answerKey) {
  return axios.get(`/user/form/answer/detail/${answerKey}`)
}

function deleteAnswerApi(answerKey) {
  return axios.post('/user/form/answer/delete', { answerKey })
}

export default {
  name: 'ProjectData',

  data() {
    return {
      loading: false,
      forms: [],
      selectedFormKey: '',
      tableData: [],
      page: 1,
      pageSize: 20,
      total: 0,
      dataStats: {
        total: 0,
        today: 0,
        week: 0,
        avgDuration: 0
      },
      detailDialogVisible: false,
      currentAnswer: null,
      currentAnswerItems: [],
      // 筛选条件
      searchKeyword: '',
      reviewFilter: null,
      appealFilter: null,  // 申诉状态筛选
      // 申诉状态映射 {answerKey: boolean}
      appealStatusMap: {},
      // 导出状态
      exporting: false,
      exportProgress: 0,
      // 申诉相关
      selectedRows: [],
      appealDialogVisible: false,
      appealSubmitting: false,
      appealForm: {
        batchName: '',
        startTime: '',
        endTime: ''
      }
    }
  },

  computed: {
    projectId() {
      return this.$route.params.projectId
    },
    // 当前选中的问卷
    selectedForm() {
      return this.forms.find(f => f.formKey === this.selectedFormKey) || null
    },
    // 是否是考试问卷 (projectType === 3 表示考试测评)
    isExamForm() {
      return this.selectedForm?.projectType === 3
    },
    // 选中答卷的审核层级（用于校验一致性）
    selectedAuditLevel() {
      if (this.selectedRows.length === 0) return null
      const levels = [...new Set(this.selectedRows.map(r => r.auditStatus || 0))]
      return levels.length === 1 ? levels[0] : null
    }
  },

  mounted() {
    this.loadForms()
  },

  watch: {
    projectId: {
      immediate: false,
      handler() {
        this.loadForms()
      }
    }
  },

  methods: {
    async loadForms() {
      if (!this.projectId) return
      try {
        const res = await pageFormRequest({ projectId: this.projectId, page: 1, size: 100 })
        console.log('[Data] pageFormRequest response:', res)
        const records = res?.data?.data?.records || res?.data?.records || res?.data?.data || []
        console.log('[Data] records:', records)
        
        // 过滤已删除的问卷 (status !== 0 或 deleted !== true)
        this.forms = records
          .filter(f => {
            // 排除已删除的问卷
            const isDeleted = f.deleted === true || f.isDeleted === true
            // status=2 可能表示已删除，根据实际情况调整
            return !isDeleted
          })
          .map(f => ({
            ...f,
            formKey: f.formKey || f.form_key || f.source_id,
            // 使用 textName 字段作为问卷名称
            name: f.textName || f.name || f.title || f.formKey || '未命名问卷',
            // 保留问卷类型（3=考试测评）— 后端返回的字段名是 type
            projectType: f.type || f.projectType || f.project_type || 0
          }))
          
        console.log('[Data] processed forms:', this.forms)
        
        // 默认选中第一个问卷
        if (this.forms.length > 0) {
          this.selectedFormKey = this.forms[0].formKey
          this.loadFormData()
        }
      } catch (e) {
        console.error('加载问卷列表失败', e)
      }
    },

    async loadFormData() {
      if (!this.selectedFormKey) {
        this.tableData = []
        return
      }
      this.loading = true
      try {
        // 构建请求参数（包含筛选条件）
        const requestParams = {
          formKey: this.selectedFormKey,
          page: this.page,
          size: this.pageSize
        }
        
        // 添加筛选参数
        if (this.searchKeyword) {
          requestParams.keyword = this.searchKeyword
        }
        if (this.reviewFilter !== null && this.reviewFilter !== '') {
          if (this.reviewFilter === 'review1') {
            requestParams.reviewNotCompleted = 1  // 一审未完成 = audit_status < 1
          } else if (this.reviewFilter === 'review2') {
            requestParams.reviewNotCompleted = 2  // 二审未完成 = audit_status < 2
          } else if (this.reviewFilter === 'review3') {
            requestParams.reviewNotCompleted = 3  // 三审未完成 = audit_status < 3
          } else if (this.reviewFilter === 'completed') {
            requestParams.reviewCompleted = 3  // 三审已完成 = audit_status >= 3
          } else {
            requestParams.auditStatus = this.reviewFilter  // 直接指定状态
          }
        }
        
        // 并行加载答卷列表和统计数据
        const [pageRes, statsRes] = await Promise.all([
          getAnswerPage(requestParams),
          getStatisticsOverview(this.selectedFormKey)
        ])

        // 处理答卷列表
        console.log('[Data] pageRes:', pageRes)
        const pageData = pageRes?.data?.data || pageRes?.data || pageRes
        console.log('[Data] pageData:', pageData)
        const records = pageData.records || pageData.rows || pageData.list || []
        console.log('[Data] records:', records)
        this.tableData = records.map(r => ({
          ...r,
          answerNo: r.answerKey?.substring(0, 8) || r.answer_key?.substring(0, 8) || '-',
          ipAddress: r.respondentIp || r.respondent_ip || r.ip || '-',
          duration: r.durationSeconds || r.duration_seconds || r.duration || 0,
          status: r.status || 2 // 已提交
        }))
        this.total = pageData.total || pageData.totalRows || 0

        // 处理统计数据
        console.log('[Data] statsRes:', statsRes)
        const stats = statsRes?.data?.data || statsRes?.data || statsRes || {}
        console.log('[Data] stats:', stats)
        this.dataStats = {
          total: stats.totalSubmits || 0,
          today: stats.todaySubmits || 0,
          week: stats.weekSubmits || 0,
          avgDuration: stats.avgDuration || 0
        }
        
        // 加载申诉状态（仅考试问卷）
        if (this.isExamForm && this.tableData.length > 0) {
          await this.loadAppealStatus()
        }
      } catch (e) {
        console.error('加载答卷数据失败', e)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },
    
    // 加载申诉状态
    async loadAppealStatus() {
      try {
        const answerKeys = this.tableData.map(r => r.answerKey).filter(k => k)
        if (answerKeys.length === 0) return
        
        const res = await axios.post('/Appeal/answer/status', answerKeys)
        const data = res?.data?.data || res?.data || res || {}
        this.appealStatusMap = data
        
        // 如果有申诉状态筛选，在前端过滤数据
        if (this.appealFilter) {
          if (this.appealFilter === 'appealed') {
            this.tableData = this.tableData.filter(r => this.appealStatusMap[r.answerKey])
          } else if (this.appealFilter === 'not_appealed') {
            this.tableData = this.tableData.filter(r => !this.appealStatusMap[r.answerKey])
          }
        }
      } catch (e) {
        console.error('加载申诉状态失败', e)
      }
    },

    // 处理搜索
    handleSearch() {
      this.page = 1
      this.loadFormData()
    },

    // 重置筛选条件
    resetFilters() {
      this.searchKeyword = ''
      this.reviewFilter = null
      this.appealFilter = null
      this.page = 1
      this.loadFormData()
    },

    // 查看答卷详情（跳转到全屏预览页面）
    viewDetail(row) {
      this.$router.push({
        path: '/answer-preview',
        query: {
          key: this.selectedFormKey,
          answerKey: row.answerKey
        }
      })
    },

    // 审核答卷（跳转到全屏阅卷页面）
    reviewAnswer(row) {
      this.$router.push({
        path: '/grading',
        query: {
          key: this.selectedFormKey,
          answerKey: row.answerKey
        }
      })
    },

    deleteAnswer(row) {
      this.$confirm('确定删除该答卷吗？此操作不可恢复！', '警告', {
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      }).then(async () => {
        try {
          await deleteAnswerApi(row.answerKey)
          this.$message.success('删除成功')
          this.loadFormData()
        } catch (e) {
          console.error('删除失败', e)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },

    async exportFormData() {
      if (!this.selectedFormKey) {
        this.$message.warning('请先选择问卷')
        return
      }
      
      this.exporting = true
      this.exportProgress = 0
      
      try {
        const formName = this.stripHtml(this.selectedForm?.name || '问卷')
        const baseURL = process.env.VUE_APP_BASE_URL || ''
        const url = `${baseURL}/user/form/export/${this.selectedFormKey}`
        const token = localStorage.getItem('ACCESS_TOKEN')
        
        // 创建 AbortController 用于超时控制
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 60000) // 60秒超时
        
        this.exportProgress = 10
        console.log('[Export] Starting export for:', formName)
        
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        })
        
        clearTimeout(timeoutId)
        this.exportProgress = 50
        
        if (!response.ok) {
          // 业务错误处理
          const text = await response.text()
          let errMsg = `导出失败 (错误码: ${response.status})`
          try {
            const errData = JSON.parse(text)
            errMsg = errData.message || errMsg
          } catch {}
          throw new Error(errMsg)
        }
        
        this.exportProgress = 70
        const blob = await response.blob()
        console.log('[Export] blob size:', blob.size)
        this.exportProgress = 90
        
        // 下载文件（使用问卷名称作为文件名）
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `${formName}_数据导出_${this.formatDateForFilename(new Date())}.xlsx`
        link.click()
        window.URL.revokeObjectURL(blobUrl)
        
        this.exportProgress = 100
        this.$message.success('导出成功')
      } catch (e) {
        console.error('[Export] Error:', e)
        // 区分错误类型
        if (e.name === 'AbortError') {
          this.$message.error('导出超时，数据量可能过大，请稍后重试或联系管理员')
        } else if (!navigator.onLine) {
          this.$message.error('网络连接已断开，请检查网络后重试')
        } else {
          this.$message.error(e.message || '导出失败，请稍后重试')
        }
      } finally {
        this.exporting = false
        this.exportProgress = 0
      }
    },

    async exportAll() {
      if (this.forms.length === 0) {
        this.$message.warning('当前项目没有问卷')
        return
      }
      
      try {
        this.$message.info('正在导出项目所有问卷数据...')
        const baseURL = process.env.VUE_APP_BASE_URL || ''
        const token = localStorage.getItem('ACCESS_TOKEN')
        const url = `${baseURL}/user/form/export/project/${this.projectId}`
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (!response.ok) {
          const text = await response.text()
          try {
            const errData = JSON.parse(text)
            this.$message.error(errData.message || `导出失败: ${response.status}`)
          } catch {
            this.$message.error(`导出失败: ${response.status}`)
          }
          return
        }
        
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `项目问卷数据_${this.projectId}.xlsx`
        link.click()
        window.URL.revokeObjectURL(blobUrl)
        this.$message.success('导出成功')
      } catch (e) {
        console.error('导出失败', e)
        this.$message.error(e.message || '导出失败')
      }
    },

    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]+>/g, '')
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      const d = new Date(timeStr)
      return d.toLocaleString('zh-CN')
    },

    formatDuration(seconds) {
      if (!seconds || seconds <= 0) return '-'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`
    },

    formatDateForFilename(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      return `${y}${m}${d}_${h}${min}`
    },

    formatAnswerValue(item) {
      if (!item.value) return '-'
      if (Array.isArray(item.value)) {
        return item.value.join(', ')
      }
      return String(item.value)
    },

    // ==================== 申诉相关方法 ====================
    
    handleSelectionChange(rows) {
      this.selectedRows = rows
    },

    getAuditLevelText(level) {
      const map = {
        0: '待审核',
        1: '一审已完成',
        2: '二审已完成',
        3: '三审已完成'
      }
      return map[level] || `${level}审`
    },

    showAppealDialog() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择答卷')
        return
      }
      
      // 校验审核层级一致性
      if (this.selectedAuditLevel === null) {
        this.$message.error('选中的答卷审核层级不一致，请选择同一层级的答卷')
        return
      }
      
      // 设置默认时间
      const now = new Date()
      const endDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
      this.appealForm.startTime = this.formatDateTimeForPicker(now)
      this.appealForm.endTime = this.formatDateTimeForPicker(endDate)
      
      this.appealDialogVisible = true
    },

    formatDateTimeForPicker(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    },

    async submitAppeal() {
      if (!this.appealForm.startTime || !this.appealForm.endTime) {
        this.$message.warning('请设置申诉时间范围')
        return
      }
      
      this.appealSubmitting = true
      try {
        const answerKeys = this.selectedRows.map(r => r.answerKey)
        const res = await axios.post('/Appeal/batch/create', {
          projectId: Number(this.projectId),
          formKey: this.selectedFormKey,
          batchName: this.appealForm.batchName,
          answerKeys,
          startTime: this.appealForm.startTime,
          endTime: this.appealForm.endTime
        })
        
        this.$message.success(`申诉下发成功，批次号：${res?.data?.batchNo || res?.batchNo || ''}`)
        this.appealDialogVisible = false
        this.selectedRows = []
        
        // 清空表格选择
        this.$refs.dataTable?.clearSelection?.()
      } catch (e) {
        console.error('下发申诉失败', e)
        this.$message.error(e?.data?.message || e?.message || '下发申诉失败')
      } finally {
        this.appealSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.project-data {
  padding: 0;
}

.status-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 500;
  }
}

.form-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;

  .label {
    color: #606266;
    font-size: 14px;
  }

  .el-select {
    width: 300px;
  }
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .stat-value {
    font-size: 28px;
    font-weight: 600;
    color: #409eff;
    margin-bottom: 6px;
  }

  .stat-label {
    font-size: 13px;
    color: #909399;
  }
}

.data-table {
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;

  i {
    font-size: 48px;
    margin-bottom: 16px;
  }

  p {
    font-size: 14px;
  }
}

.text-danger {
  color: #f56c6c;
}

.answer-detail {
  .detail-header {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;

    .info-item {
      label {
        color: #909399;
      }
      span {
        color: #303133;
      }
    }
  }

  .answer-item {
    margin-bottom: 16px;

    .question-title {
      font-weight: 500;
      color: #303133;
      margin-bottom: 6px;
    }

    .answer-value {
      padding: 8px 12px;
      background: #f5f7fa;
      border-radius: 4px;
      color: #606266;
    }
  }
}

/* 筛选区域 */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

/* 审核状态图标 */
.review-done {
  color: #67c23a;
  font-size: 16px;
  font-weight: bold;
}

.review-pending {
  color: #c0c4cc;
  font-size: 16px;
}

/* 导出进度遮罩 */
.export-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.export-progress-card {
  background: #fff;
  padding: 32px 48px;
  border-radius: 12px;
  text-align: center;
  min-width: 320px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

  i {
    font-size: 36px;
    color: #409eff;
    animation: spin 1s linear infinite;
  }

  .export-title {
    margin: 16px 0 12px;
    font-size: 16px;
    font-weight: 500;
    color: #303133;
  }

  .export-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 12px;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
